import { useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { usePatientStore } from '../stores/patientStore'
import { useNotificationStore } from '../stores/notificationStore'
import { api } from '../services/api'

/**
 * CCOW context synchronization hook.
 *
 * Listens for `context.changed` WebSocket events from the server
 * (triggered when another application sets the patient context) and:
 *   - Updates `patientStore.currentPatient`
 *   - Navigates to the patient chart if the user is on a patient page
 *   - Shows a brief toast notification
 *
 * When the USER changes patient (opens a chart), this hook also
 * POSTs to `/api/v1/context/set` to notify other connected apps.
 */
export function useContextSync() {
  const navigate = useNavigate()
  const location = useLocation()
  const currentPatient = usePatientStore((s) => s.currentPatient)
  const setCurrentPatient = usePatientStore((s) => s.setCurrentPatient)
  const notifications = useNotificationStore((s) => s.notifications)
  const prevPatientIdRef = useRef<number | null>(null)

  // Listen for context.changed events from the notification store
  // (WebSocket events are pushed into the notification store by useWebSocket)
  useEffect(() => {
    if (notifications.length === 0) return

    const latest = notifications[0]
    if (latest.notificationType !== 'context.changed') return

    // Parse the payload
    try {
      const payload = typeof latest.message === 'string' ? JSON.parse(latest.message) : latest.message
      const patientId = payload.patientId ?? payload.patient_id
      const patientName = payload.patientName ?? payload.patient_name
      const patientMrn = payload.patientMrn ?? payload.patient_mrn

      if (!patientId) return

      // Avoid re-processing if this is a context we set ourselves
      if (currentPatient?.patientId === patientId) return

      // Update the patient store with the new context
      setCurrentPatient({
        patientId,
        mrn: patientMrn ?? '',
        fullName: patientName ?? 'Unknown',
        dateOfBirth: '',
        gender: '',
        allergies: [],
        codeStatus: null,
        isVip: false,
        isActive: true,
      })

      // Show toast notification
      toast.info(`Context changed to ${patientName ?? `Patient #${patientId}`}`)

      // If user is on a patient page, navigate to the new patient
      if (location.pathname.startsWith('/patients/')) {
        navigate(`/patients/${patientId}/demographics`)
      }
    } catch {
      // Ignore parse errors for non-context notifications
    }
  }, [notifications, currentPatient, setCurrentPatient, navigate, location.pathname])

  // Notify the server when the user changes patient context
  const syncContextToServer = useCallback(
    async (patientId: number, encounterId?: number) => {
      try {
        await api.context.set({ patientId, encounterId })
      } catch {
        // Silently fail — CCOW sync is best-effort
      }
    },
    []
  )

  // Watch for patient changes and sync to server
  useEffect(() => {
    const patientId = currentPatient?.patientId ?? null

    if (patientId && patientId !== prevPatientIdRef.current) {
      prevPatientIdRef.current = patientId
      syncContextToServer(patientId)
    } else if (!patientId) {
      prevPatientIdRef.current = null
    }
  }, [currentPatient, syncContextToServer])

  return { syncContextToServer }
}
